import { forwardRef, InputHTMLAttributes, Ref } from 'react';
import styled, { css } from 'styled-components';
import { tokens } from 'styles/tokens';

export type TextInputProps = InputHTMLAttributes<HTMLInputElement> & {
    /**
     * Uses aria-invalid to mark the field as invalid and be styled as such.
     */
    invalid?: boolean;
    /**
     * The type of text input. Defaults to `text`.
     */
    type?: 'text' | 'search' | 'email' | 'password' | 'hidden' | 'number';
    /**
     * Whether or not the input should be automatically focused when it is rendered.
     */
    autofocus?: boolean;
    /**
     * Whether or not the text input can be edited.
     */
    readonly?: boolean;
    /**
     * Whether or not to show a green underline when the input is not invalid. Defaults to `false`.
     */
    showValidState?: boolean;

    pattern?: string;
    /**
     * The inputmode global attribute is an enumerated attribute that hints at the type of data that
     * might be entered by the user while editing the element or its contents.
     */
    inputMode?: 'text' | 'decimal' | 'numeric';

    /**
     * The padding to apply inside of the text input. Must be a valid CSS unit. Defaults to `1rem`.
     *
     * It is preferable to use this over setting padding through CSS because this handles layout
     * shift due to the border changing size.
     */
    padding?: string;

    /**
     * The width of the text input. Must be a valid CSS unit.
     */
    width?: string;

    /** default: underline */
    appearance?: 'rounded' | 'underline';
};

const dontPassProps = ['showValidState', 'padding', 'width'];
/**
 * The default padding to provide around the text inside the input.
 */
export const defaultInputPadding = '1rem';

type AppearanceConfig = {
    border: string;
    padding: string;
    focusStyle: string;
    invalidStyle: string;
};

function buildAppearance(
    appearance: 'rounded' | 'underline',
    padding: string,
): AppearanceConfig {
    if (appearance === 'rounded') {
        return {
            border: `border: 1px solid ${tokens.input.border}; border-radius: 3px;`,
            padding: 'padding: 1rem 0.8rem;',
            focusStyle: `outline: 2px solid ${tokens.border.focus};`,
            invalidStyle: `
                outline: 1px solid ${tokens.border.error};
                &:focus { outline-width: 2px; }
            `,
        };
    }
    return {
        border: `border: 0; border-bottom: 1px solid ${tokens.input.border};`,
        // extra 1px prevents layout shift when border grows to 2px on focus/invalid
        padding: `padding: ${padding} 0; padding-bottom: calc(${padding} + 1px);`,
        focusStyle: `
            border-color: ${tokens.border.focus};
            border-width: 2px;
            padding-bottom: ${padding};
        `,
        invalidStyle: `
            border-color: ${tokens.border.error};
            border-width: 2px;
            padding-bottom: ${padding};
        `,
    };
}

const underlineValidStyle = (padding: string) => css`
    &[aria-invalid='false'] {
        border-color: ${tokens.border.success};
        border-width: 2px;
        padding-bottom: ${padding};
    }
`;

const Input = styled.input.withConfig({
    shouldForwardProp: (p) => !dontPassProps.includes(p),
})<TextInputProps & { $appearance: 'rounded' | 'underline' }>`
    ${({ $appearance, padding: p }) => {
        const a = buildAppearance($appearance, p ?? defaultInputPadding);
        return css`
            box-sizing: border-box;
            ${a.border}
            ${a.padding}
            background: inherit;
            color: ${tokens.text.primary};
            outline: none;

            &:focus {
                ${a.focusStyle}
            }

            &[aria-invalid='true'] {
                ${a.invalidStyle}
            }
        `;
    }}

    ${({ width }) => width && `width: ${width};`}

    &:disabled {
        color: ${tokens.text.disabled};
    }

    &::placeholder {
        color: ${tokens.text.secondary};
    }

    &:disabled::placeholder {
        color: ${tokens.text.disabled};
    }

    ${({ showValidState, padding, $appearance }) =>
        showValidState &&
        $appearance === 'underline' &&
        underlineValidStyle(padding ?? defaultInputPadding)}
`;

function TextInput(
    {
        type,
        invalid,
        readonly,
        autofocus,
        showValidState,
        autoComplete = 'off',
        appearance = 'underline',
        ...rest
    }: TextInputProps,
    ref: Ref<HTMLInputElement>,
) {
    return (
        <Input
            ref={ref}
            type={type ?? 'text'}
            aria-invalid={invalid}
            readOnly={readonly}
            autoFocus={autofocus}
            showValidState={showValidState ?? false}
            autoComplete={autoComplete}
            $appearance={appearance}
            {...rest}
        />
    );
}

const forwardRefTextInput = forwardRef(TextInput);

export { forwardRefTextInput as TextInput };
